import React, { useRef, useState } from "react";
import { Upload, Link2, ImageIcon, X, Scan } from "lucide-react";

export default function SearchBox({ onSubmitImage, onSubmitUrl, loading }) {
  const [tab, setTab] = useState("image");
  const [imgB64, setImgB64] = useState(null);
  const [imgName, setImgName] = useState("");
  const [url, setUrl] = useState("");
  const [note, setNote] = useState("");
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const onPick = (file) => {
    if (!file) return;
    const ok = ["image/jpeg", "image/png", "image/webp"].includes(file.type);
    if (!ok) {
      alert("Formats supportés : JPG, PNG, WEBP");
      return;
    }
    const r = new FileReader();
    r.onload = () => {
      setImgB64(r.result);
      setImgName(file.name);
    };
    r.readAsDataURL(file);
  };

  const submit = (e) => {
    e.preventDefault();
    if (loading) return;
    if (tab === "image") {
      if (!imgB64) return;
      onSubmitImage(imgB64, note);
    } else {
      if (!url.trim()) return;
      onSubmitUrl(url.trim(), note);
    }
  };

  return (
    <form
      data-testid="search-box"
      onSubmit={submit}
      className="bg-white border border-ink shadow-brut-lg"
    >
      {/* Tabs */}
      <div className="flex border-b border-ink">
        <button
          type="button"
          data-testid="tab-image"
          onClick={() => setTab("image")}
          className={`flex-1 py-3 px-4 text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition ${
            tab === "image" ? "bg-ink text-linen" : "bg-white hover:bg-mist/40"
          }`}
        >
          <ImageIcon className="w-4 h-4" /> Image
        </button>
        <button
          type="button"
          data-testid="tab-url"
          onClick={() => setTab("url")}
          className={`flex-1 py-3 px-4 text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 border-l border-ink transition ${
            tab === "url" ? "bg-ink text-linen" : "bg-white hover:bg-mist/40"
          }`}
        >
          <Link2 className="w-4 h-4" /> Lien Pinterest / Insta / TikTok
        </button>
      </div>

      <div className="p-5 md:p-6 space-y-4">
        {tab === "image" ? (
          <div>
            {!imgB64 ? (
              <label
                data-testid="dropzone"
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  onPick(e.dataTransfer.files?.[0]);
                }}
                className={`block cursor-pointer border-2 border-dashed ${
                  dragOver ? "border-poppy bg-poppy/5" : "border-ink/40"
                } p-10 text-center hover:bg-mist/30 transition`}
              >
                <input
                  ref={inputRef}
                  data-testid="file-input"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  hidden
                  onChange={(e) => onPick(e.target.files?.[0])}
                />
                <Upload className="w-7 h-7 mx-auto mb-3 text-ink/60" />
                <div className="font-display font-bold text-lg">
                  Glissez une capture ici
                </div>
                <div className="text-sm text-ink/60 mt-1">
                  ou cliquez pour parcourir — JPG / PNG / WEBP
                </div>
              </label>
            ) : (
              <div className="relative border border-ink p-3 flex items-center gap-3">
                <img
                  src={imgB64}
                  alt="aperçu"
                  className="w-16 h-16 object-cover border border-ink"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold truncate">{imgName}</div>
                  <div className="text-xs text-ink/60">Prêt à analyser</div>
                </div>
                <button
                  type="button"
                  data-testid="clear-image-button"
                  onClick={() => {
                    setImgB64(null);
                    setImgName("");
                  }}
                  className="p-1.5 border border-ink hover:bg-ink hover:text-linen"
                  aria-label="Retirer l'image"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        ) : (
          <div>
            <label className="tape-label block mb-1.5">URL Pinterest / Instagram / TikTok / ...</label>
            <input
              data-testid="url-input"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.pinterest.com/pin/..."
              className="input-brut w-full px-3 py-2.5 text-sm"
            />
            <p className="text-xs text-ink/50 mt-2">
              On extrait l'image principale puis on la fait analyser par l'IA.
            </p>
          </div>
        )}

        <div>
          <label className="tape-label block mb-1.5">Note (optionnel)</label>
          <input
            data-testid="note-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder='ex: "je cherche la robe au centre"'
            className="input-brut w-full px-3 py-2.5 text-sm"
          />
        </div>

        <button
          data-testid="search-submit-button"
          type="submit"
          disabled={loading || (tab === "image" ? !imgB64 : !url.trim())}
          className="btn-brut w-full py-3.5 text-sm uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Scan className="w-4 h-4" />
          {loading ? "Analyse en cours…" : "Trouver où l'acheter"}
        </button>
      </div>
    </form>
  );
}
